import { BillingEventData } from "@/common/types/billing/event.type";
import React from "react";
import { formatCurrency, formatDateTime, formatDecimal, formatUnit } from "@/common/utils/formatters";
import EstablishmentConsumptionDetails from "../components/EstablishmentConsumptionDetails";
import EstablishmentConsumptionBuyer from "../components/EstablishmentConsumptionBuyer";
import EstablishmentConsumptionTotalFee from "../components/EstablishmentConsumptionTotalFee";
import EstablishmentRefundDetails from "../components/EstablishmentRefundDetails";
import { transformToNumber } from "@/common/utils/transformers";
import PrintReceiptButton from "@/common/components/PrintReceiptButton";
import CardNumberWithCopyButton from "@/common/components/CardNumberWithCopyButton";
import { ClientFleetCardData } from "@/common/types/client/card.type";

export class EstablishmentBillingEventTransformer {
  static tableTransformer() {
    return (events: BillingEventData[]) => {
      return events.map((event) => {
        const originalEventable = event.eventable
        const eventable = originalEventable?.refundable
          ?? originalEventable?.related_eventable?.refundable
          ?? originalEventable?.related_eventable
          ?? originalEventable;

        const detailsComponent = eventableDetailsComponent[event.eventable_type];

        return {
          protocol_number: event.protocol_number,
          bill: '',
          eventable_type: eventableTypeTranslations[event.eventable_type] || 'Nao implementado',
          details: detailsComponent
            ? React.createElement(detailsComponent, { data: event.eventable })
            : '',
          total_amount: event.total_amount
            ? formatCurrency(
              transformToNumber({
                value: event.total_amount
              }) + transformToNumber({ value: event.fee_total, invert: true })
            )
            : '',
          fee_total_amount: event.fee_total
            ? React.createElement(EstablishmentConsumptionTotalFee, { data: event })
            : '',
          income_tax_total: event.income_tax_total ? formatCurrency(parseFloat(event.income_tax_total)) : '',
          card: eventable?.card ? (
            <CardNumberWithCopyButton card={eventable?.card} />
          ) : '',
          usage_declaration: eventable?.current_equipment_usage
            ? formatUnit(formatDecimal(eventable?.current_equipment_usage, 0), eventable?.current_equipment_usage_unit)
            : '',
          buyer: eventable?.buyer
            ? React.createElement(EstablishmentConsumptionBuyer, { data: eventable.buyer })
            : '',
          license_plate: (eventable?.card as ClientFleetCardData)?.equipment_field_for_display_value || '',
          capture_method: eventable?.capture_method?.display_name || originalEventable?.capture_method?.display_name || '--',
          created_at: formatDateTime(new Date(event?.created_at || 0)),
          actions: (
            ['Client::Consumption', 'Billing::Refund'].includes(event.eventable_type) ? <PrintReceiptButton eventId={event.protocol_number} showConfirmationModal /> : null
          ),
        };
      });
    };
  }

  static quickSearchModelTransformer() {
    return (events: BillingEventData[]) => {
      return events.map((event) => ({
        route: `establishments/${event.company_id}/events?fromInternalBase%5Blabel%5D=Todas&fromInternalBase%5Bvalue%5D=&search=${event.protocol_number}`,
        items: [
          ['Transação', event.protocol_number],
          ['Tipo', eventableTypeTranslations[event.eventable_type]],
          ['Estabelecimento', event.eventable?.establishment_branch?.general_information?.fantasy_name],
          ['Detalhes', eventableDetailsQuickSearchValue[event.eventable_type] ? eventableDetailsQuickSearchValue[event.eventable_type](event) : ''],
        ],
      }));
    };
  }
}

const eventableDetailsComponent: { [key: string]: React.ComponentType<any> } = {
  'Client::Consumption': EstablishmentConsumptionDetails,
  'Billing::Refund': EstablishmentRefundDetails,
  'Billing::TefTransaction': EstablishmentConsumptionDetails,
};

const eventableTypeTranslations: { [key: string]: string } = {
  'Client::Consumption': 'Consumo',
  'Billing::Refund': 'Estorno',
  'Billing::TefTransaction': 'Tarifa TEF',
};

export const eventableDetailsQuickSearchValue: { [key: string]: (event: BillingEventData) => string; } = {
  'Client::Consumption': (event) => `Empresa cliente: ${event?.eventable?.client_branch?.general_information?.fantasy_name}`,
  'Billing::Refund': (event) => `N. Protocolo: ${event?.eventable?.refundable?.protocol_number}`,
  'Billing::TefTransaction': (event) => `Estabelecimento: ${event?.eventable?.establishment_branch?.general_information?.fantasy_name}`,
};
